import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
    children: ReactNode;
}

interface State {
    error: Error | null;
}

/**
 * Catches render errors so we show an error icon and message instead of a white screen.
 * Errors are still logged to the console in componentDidCatch.
 */
export class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('ErrorBoundary caught:', error, errorInfo);
    }

    render(): ReactNode {
        if (this.state.error) {
            return (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-[#091034] [--focus-ring:#FFFFFF]"
                    role="alert"
                >
                    <div className="flex flex-col items-center gap-5 max-w-sm text-center">
                        <span
                            className="material-symbols-rounded !text-7xl text-red-400"
                            aria-hidden
                        >
                            error
                        </span>
                        <p className="text-white text-lg">Something went wrong.</p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <Button
                                variant="outline"
                                className="border-white/80 text-white hover:bg-white hover:text-[#091034] bg-transparent light:focus-visible:outline-white"
                                onClick={() => window.location.reload()}
                            >
                                Reload
                            </Button>
                            <a
                                href="/"
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-white text-[#091034] hover:bg-gray-200 h-10 px-4 py-2 transition-colors"
                            >
                                Go to home
                            </a>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
